import { useState, useEffect } from 'react';
import { userTypesString, userTypes } from '../../app/enums';
import { Grid, Button, TextField, Checkbox } from '@material-ui/core';
import { DialogActions, DialogContentText, DialogContent, Dialog, DialogTitle} from '@material-ui/core';
import { FormHelperText, FormControl, Select, MenuItem, InputLabel, FormControlLabel } from '@material-ui/core/';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import { SuccessDialog } from '../Dialogs/SuccessDialog';
import { ConfirmSubmitDialog } from '../Dialogs/ConfirmSubmitDialog';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Authentication, Users, Locations } from '../../api/api';
import { UserType } from '../../models/user.interface';
import { Location } from '../../models/location.interface';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { defaultUserType } from '../../pages/Users';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    gridSpacer: {
      minHeight: '12px',
    },  
  }),
);

interface IEditUserDialog {
  open: boolean;
  onClose: () => void;
  user: UserType;
  locationList: Location[]
}

export const EditUserDialog = ({
  open, onClose, user, locationList
}: IEditUserDialog) => {
  const classes = useStyles(); 
  const [ userSaved, setUserSaved ] = useState<boolean>(false);
  const [ locSaved, setLocSaved ] = useState<boolean>(false);
  const [ reset, setReset ] = useState<boolean>(false);
  const [ locationUpdated, setLocationUpdated ] = useState(false);
  const [ userUpdated, setUserUpdated ] = useState<UserType>(user)
  const [ userErrors, setUserErrors ] = useState<UserType>(defaultUserType);
  const [ serviceError, setServiceError ] = useState<string>('');
  const [ locationError, setLocationError ] = useState<string>('');
  const [ userLocation, setUserLocation ] = useState<Location>();

  useEffect(() => {
    setUserUpdated(user)
    setUserLocation(user.locations[0])
    setLocationUpdated(false)
   
    if(user.userType === userTypes.EXTERNAL && isEmpty(user.locations[0])){
      setLocationError('Required');
    }
  }, [user])

  const onCloseHandler = () => {
    onClose() 
    setUserSaved(false)
    setLocSaved(false)
  }

  const handleChange = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement
    setUserUpdated({...userUpdated, [target.id]: target.value})
  }

  const handleUserTypeChange = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement
    
    if(target.value === userTypes.INTERNAL){
      setLocationUpdated(true)
    }

    setUserUpdated({...userUpdated, userType: target.value})
  }

  const handleEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserUpdated({...userUpdated, enabled: e.target.checked})
  };

  const validateField = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement

    if(target.value === undefined || target.value.length < 1)
      setUserErrors({...userErrors, [target.id]: 'Required'})
    else
      setUserErrors({...userErrors, [target.id]: ''})
  }

  const validateEmail = () => {
    if(isEmpty(user.email)){
      setUserErrors({
        ...userErrors, 
        email: 'Required'
      })
    } else if(userUpdated.email.search(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)){
      setUserErrors({
        ...userErrors, 
        email: 'This must be in a valid email format'
      })
    } else{
      setUserErrors({...userErrors, email: ''})
      return true;
    }

    return false;
  }

  const validateLocation = () => {
    if(userUpdated.userType === userTypes.EXTERNAL && isEmpty(userLocation)){
      setLocationError('Required')
      return false;
    } else {
      setLocationError('')
      return true;
    }
  }

  const isFormValid = () => {
    let field: keyof UserType;

    for(field in userErrors){
      if(!isEmpty(userErrors[field])){
        return false;
      }   
    }
    
    if(!validateLocation())
      return false;
    
    if(!validateEmail())
      return false;

    return true;
  }

  const handleEditUserSubmitClick = () => {
    if(!isFormValid()){
      return;
    }
    
    if(user !== userUpdated){
      Users.updateUser(
        userUpdated.id,
        userUpdated.userName,
        userUpdated.userType,
        userUpdated.firstName,
        userUpdated.lastName,
        userUpdated.email,
        userUpdated.enabled
      ).then((data) => {
        setUserSaved(true);
      }).catch((userSaveError) => {
        setUserSaved(false);
        setServiceError(userSaveError.response.data.message)
      })
    } else {
      setUserSaved(true);
    }

    if(locationUpdated){
      if(isEmpty(user.locations) && userLocation?.id){
        Locations.addLocation(userUpdated.id, userLocation?.id)
          .then(() => {
            setLocSaved(true);
          })
          .catch((e) =>{
            setLocSaved(false);
            setServiceError(e.response.data.message)
          })
      } else {
        if(!isEmpty(user.locations)){
          Locations.deleteUserLocation(userUpdated.id, user.locations[0].id)
          .then(() =>{
            if(userUpdated.userType === userTypes.INTERNAL)
              setLocSaved(true);
          })
          .catch((e) =>{
            setServiceError(e.response.data.message)
          })
        }    

        if(userUpdated.userType !== userTypes.INTERNAL && userLocation?.id){
          Locations.addLocation(userUpdated.id, userLocation.id)
          .then(() => {
            setLocSaved(true)
          })
          .catch((e) =>{
            setLocSaved(false);
            setServiceError(e.response.data.message)
          })
        } else {
          setLocSaved(true)
        }
      } 
    }else{
      setLocSaved(true)
    }
    
    setLocationUpdated(false)
  }
  
  const handleLocationChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value:string = event.target.value as string;
    setLocationUpdated(true)
    if(value === '-1'){
      setUserLocation(undefined)
    } else {
      const newLoc: Location[] = locationList.filter(loc => loc.id === value)
      setUserLocation(newLoc[0])
    }
  }

  function handleResetPassword(){
    Authentication.emailPasswordReset(userUpdated.userName)
    .then((data) => {
      setReset(true)
    }).catch(e => {
      setServiceError(e.response.data.message)
    })
  }

  return ( 
    <div>
      <SuccessDialog 
        open={userSaved && locSaved && serviceError.length === 0}
        onClose={onCloseHandler}
        title='Edit User'
        content='User Updated'
      />
      <ConfirmSubmitDialog 
        open={reset}
        onClose={() => setReset(false)}
        title='Reset Password'
        content='Reset password email sent.'
      />
      <Dialog 
        onClose={onCloseHandler} 
        aria-labelledby="edit-product-title" 
        aria-describedby="edit-product-text"
        open={open}
      >
        <DialogTitle id="edit-product-title">Edit User</DialogTitle>
        <DialogContent>
          <DialogContentText id="edit-user-text">   
              { serviceError.length > 0 
                &&
                <Grid item xs={12}>
                  <Alert severity="error" >
                    <AlertTitle>Error</AlertTitle>
                    {serviceError}
                  </Alert>   
                  <Grid className={classes.gridSpacer} />
                </Grid>
              } 
              <TextField
                id="userName"
                label="User Name"
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                disabled
                value={userUpdated.userName}
              />
              <TextField
                id="firstName"
                label="First Name"
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                value={userUpdated.firstName}
                onChange={handleChange} 
                onBlur={validateField}
                error={!isEmpty(userErrors.firstName)}
                helperText={userErrors.firstName}
                required
              />
              <TextField
                id="lastName"
                label="Last Name"
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                value={userUpdated.lastName}
                onChange={handleChange} 
                onBlur={validateField}
                error={!isEmpty(userErrors.lastName)}
                helperText={userErrors.lastName}
                required
              />
              <TextField
                id="email"
                label="Email"
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                value={userUpdated.email}
                onChange={handleChange} 
                onBlur={validateEmail}
                error={!isEmpty(userErrors.email)}
                helperText={userErrors.email}
                required
              />
              <FormControl 
                variant="outlined" 
                margin="normal"
                fullWidth 
                error={!isEmpty(userErrors.userType)}   
                required
              >
                <InputLabel id="userTypeLabel">User Type</InputLabel>
                <Select
                  labelId="userTypeLabel"
                  onChange={handleUserTypeChange} 
                  onBlur={validateField}
                  defaultValue="-1"
                  labelWidth={80}
                  id="userType"
                  value={userUpdated.userType}
                >
                  <MenuItem value="-1">
                    <em>Select User Type</em>
                  </MenuItem>
                  {Object.keys(userTypesString).map((userType) => (
                    <MenuItem key={userType} value={userType}>
                      {userTypesString[userType as keyof typeof userTypesString]}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{userErrors.userType}</FormHelperText>
              </FormControl>
              { userUpdated.userType === userTypes.EXTERNAL &&
                <FormControl 
                  variant="outlined" 
                  margin="normal"
                  fullWidth 
                  error={!isEmpty(locationError)}
                  required
                >
                  <InputLabel id="locationsLabel">Location</InputLabel>
                  <Select
                    labelId="locationsLabel"
                    id="locations"
                    onChange={handleLocationChange} 
                    onBlur={validateLocation}
                    labelWidth={80}
                    defaultValue="-1"
                    value={userLocation?.id || "-1"}
                  >
                    <MenuItem value="-1">
                      <em>Select Location</em>
                    </MenuItem>
                    {locationList.map((loc) => (
                      <MenuItem value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{locationError}</FormHelperText>
                </FormControl>
              }
              <FormControlLabel control={
                <Checkbox
                  checked={userUpdated.enabled}
                  onChange={handleEnabled}
                  inputProps={{ 'aria-label': 'controlled' }}
                  color="primary"
                />
              } label="Enable" />
            </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="space-between">
            <Grid xs={8}>
              <Button 
                onClick={handleResetPassword} 
                color="primary"
                variant="outlined"
              >
                Reset Password
              </Button>
            </Grid>
            <Grid xs={1}>
              <Button 
                onClick={onCloseHandler} 
                color="primary"
                variant="outlined"
              >
                Cancel
              </Button>
            </Grid>
            <Grid>
              <Button 
                variant="contained"
                color="primary"
                size="small" 
                startIcon={<SaveOutlinedIcon/>}
                onClick={handleEditUserSubmitClick}
              >
                Save 
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>   
    </div>
  );
}

export default EditUserDialog; 